import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import includes from 'lodash/includes';
import find from 'lodash/find';
import { withRouter, Link } from 'react-router-dom';
import { localized } from 'data/Localization';
import Exit from 'assets/images/exit.svg';
import NavigationItem from 'components/Molecules/Navigation/NavigationItem';
import FAQNG4 from 'assets/images/faq-ng4.svg';
import ServiceDeskNG4 from 'assets/images/service-desk-ng4.svg';
import { resetPlan, resetUser, toggleNavigation } from 'Redux/actions';
import AvatarSmall from 'components/Icons/AvatarSmall';
import PlusGradient from 'components/Icons/PlusGradient';
import Chevron from 'components/Icons/Chevron';
import Plan from 'components/Icons/Plan';
import ProductGradient from 'components/Icons/ProductGradient';
import ReferFarmerIcon from 'components/Icons/ReferFarmerIcon';
import MyApplicationsIcon from 'components/Icons/MyApplicationsIcon';
import MyApplicationMapIcon from 'components/Icons/MyApplicationMapIcon';
import NutriguideLogo from 'components/Icons/NutriguideLogo';
import MyPlotsMenuIcon from 'components/Icons/MyPlotsMenuIcon';
import CropRotationIcon from 'components/Icons/CropRotationIcon';
import UserManualIcon from 'components/Icons/UserManualIcon';
import TutorialVideosIcon from 'components/Icons/TutorialVideosIcon';
import { logout } from 'Utils/MSAL_AuthUtils';
import { getGradientCropIcon } from 'Utils/CropPictoUtils';
import { getMyOverviewLink } from 'Utils/GlobalUtils';
import { countryBehaviour, localeBehaviour } from 'data/Behaviour';
import UserUtils from 'Utils/UserUtils';

const Navigation = ({
  user: { locale, id_token, country, role, firstName, userName, lastName, isOnboarding },
  location,
  resetUser,
  UI: { isNavOpen, selectedHarvestingYear },
  myPlans,
  history,
  toggleNavigation,
}) => {
  const historyListener = history.listen(() => {
    toggleNavigation('');
  });

  useEffect(() => () => historyListener(), [historyListener]);

  const navOpenOrClosedClassName = () => {
    let className = '';
    if (isNavOpen) {
      className = 'navigation-open';
    } else if (isNavOpen === '') {
      className = '';
    } else if (isNavOpen !== null) {
      className = 'navigation-close';
    }
    return className;
  };

  const getPlanIcon = () => {
    let planIcon = null;
    const planIdMatch = document.location.href.split('/myplan/');
    if (myPlans.data && planIdMatch && myPlans.data.raw) {
      const planId = planIdMatch[1];
      let myPlan = [];
      myPlan = find(myPlans.data.raw, (o) => o.id === planId);
      if (myPlan) {
        planIcon = (
          <div className="main-nav__item__picto">
            {getGradientCropIcon(myPlan.cropLogoId, { className: 'main-nav__picto-svg' })}
          </div>
        );
      }
    }
    return planIcon;
  };

  const authenticated = id_token != null;

  return (
    <Grid item sm lg={3} className={`main-nav__wrapper ${navOpenOrClosedClassName()}`}>
      <div className="main-nav__header">
        <Link
          to={role ? getMyOverviewLink(role, null, selectedHarvestingYear) : '/welcome'}
          alt={localized(locale, 'my_overview_title')}
          id="nav_overview_logo"
          className="logo"
        >
          <NutriguideLogo tagline={false} className="logo__img" />
        </Link>

        <p className="main-nav__hello-user">
          {authenticated ? (
            <Link
              className={[
                'main-nav__user-link',
                location.pathname === '/myaccount' ? 'main-nav__link--active' : null,
              ]
                .join(' ')
                .trim()}
              to="/myaccount"
              alt={localized(locale, 'my_account')}
              id="nav_account"
            >
              <span className="main-nav__avatar">
                <AvatarSmall className="main-nav__avatar__picto" />
              </span>
              <span>
                {firstName || userName} {lastName || null}
              </span>
            </Link>
          ) : (
            <Link
              className={[
                'main-nav__user-link',
                location.pathname === '/myaccount' ? 'main-nav__link--active' : null,
              ]
                .join(' ')
                .trim()}
              to="/"
              alt={localized(locale, 'my_account')}
              id="nav_account"
              onClick={() => {
                resetUser();
                localStorage.clear();
              }}
            >
              <Chevron className="picto-chevron--left" fill="#71bf44" />
              {localized(locale, 'homescreen')}
            </Link>
          )}
        </p>
      </div>

      <nav className="main-nav">
        <div className="main-nav__list-links">
          <h2 className="main-nav__title">{localized(locale, 'manage_my_plots')}</h2>
          {countryBehaviour(country, 'my_plots_functionalities') && (
            <NavigationItem
              id="nav_plots"
              title={localized(locale, 'my_plots')}
              itemClass={location.pathname.match(/myplots/) ? 'main-nav__link--active' : null}
              link="/myplots"
              picto={<MyPlotsMenuIcon />}
              active={authenticated && role && !isOnboarding}
            />
          )}
          {countryBehaviour(country, 'crop_rotation') && (
            <>
              <NavigationItem
                id="my_crop_rotations"
                title={localized(locale, 'my_crop_rotation')}
                itemClass={
                  location.pathname.match(/my-crop-rotation/) ? 'main-nav__link--active' : null
                }
                link="/my-crop-rotation"
                picto={<CropRotationIcon />}
                active={authenticated && role && !isOnboarding}
              />
              <NavigationItem
                id="add_crop_rotation"
                title={localized(locale, 'add_crop_rotation')}
                itemClass={
                  location.pathname.match(/add-crop-rotation/) ? 'main-nav__link--active' : null
                }
                link="/add-crop-rotation"
                picto={<PlusGradient />}
                active={authenticated && role && !isOnboarding}
              />
            </>
          )}
          {countryBehaviour(country, 'application_map_functionality') && (
            <NavigationItem
              id="my_application_maps"
              title={localized(locale, 'my_application_maps')}
              itemClass={
                location.pathname.match(/my-application-maps/) ? 'main-nav__link--active' : null
              }
              link="/my-application-maps"
              picto={<MyApplicationMapIcon />}
              active={authenticated && role && !isOnboarding}
            />
          )}
          <h2 className="main-nav__title">{localized(locale, 'manage_my_plans')}</h2>
          <NavigationItem
            id="nav_plans"
            title={localized(locale, 'my_plans')}
            itemClass={
              !location.pathname.match(/myplan/) &&
              (includes(location.pathname, '/myoverview') || includes(location.pathname, '/myplan'))
                ? 'main-nav__link--active'
                : null
            }
            link={getMyOverviewLink(role, null, selectedHarvestingYear)}
            picto={<Plan />}
            active={authenticated && !!role}
          />
          {authenticated && location.pathname.match(/myplan/) && (
            <NavigationItem
              id="nav_application"
              title={localized(locale, 'fertilization_plan_title')}
              itemClass="main-nav__link--active"
              link={location.pathname}
              picto={getPlanIcon()}
              active
            />
          )}
          <NavigationItem
            id="nav_add_plan"
            title={localized(locale, 'add_a_plan')}
            itemClass={
              includes(location.pathname, '/plan-builder')
                ? 'main-nav__link--active main-nav__link--active--add'
                : null
            }
            link="/plan-builder/flow-select"
            picto={<PlusGradient />}
            active
          />
          <NavigationItem
            id="nav_my_fertilizer_needs"
            title={localized(locale, 'my_fertilizer_needs')}
            itemClass={
              includes(location.pathname, '/fertilizer-needs')
                ? 'main-nav__link--active main-nav__link--active--add'
                : null
            }
            link="/fertilizer-needs"
            picto={<ProductGradient />}
            active={
              authenticated &&
              role &&
              !isOnboarding &&
              countryBehaviour(country, 'fertilizer_needs')
            }
          />
          <NavigationItem
            id="nav_my_applications"
            title={localized(locale, 'tab_my_applications')}
            itemClass={
              includes(location.pathname, '/my-applications')
                ? 'main-nav__link--active main-nav__link--active--add'
                : null
            }
            link="/my-applications"
            picto={<MyApplicationsIcon clipPathId="my-applications-clip-path-1" />}
            active={
              authenticated && role && !isOnboarding && countryBehaviour(country, 'my_applications')
            }
          />
        </div>
        {role === 'reseller' && authenticated && (
          <div className="main-nav__list-links">
            <h2 className="main-nav__title">{localized(locale, 'manage_products')}</h2>
            <NavigationItem
              id="nav_add_product"
              title={localized(locale, 'my_products')}
              link="/product/overview"
              itemClass={
                location.pathname.includes('/product/overview') ? 'main-nav__link--active' : null
              }
              picto={<ProductGradient />}
              active={role === 'reseller' && authenticated}
            />
            <NavigationItem
              id="nav_add_product"
              title={localized(locale, 'add_a_product')}
              link="/product/add"
              itemClass={
                location.pathname.includes('/product/add') ? 'main-nav__link--active' : null
              }
              picto={<PlusGradient />}
              active={
                role === 'reseller' && authenticated && countryBehaviour(country, 'add_product')
              }
            />
          </div>
        )}
        <div className="main-nav__list-links">
          <h2 className="main-nav__title">{localized(locale, 'need_help')}</h2>
          <NavigationItem
            id="nav_user_manual"
            title={localized(locale, 'user_manual')}
            picto={<UserManualIcon />}
            onClick={(e) => {
              e.preventDefault();
              window.open(localeBehaviour(locale, 'user_manual_pdf'));
            }}
            active={UserUtils.isNG4()}
          />
          {countryBehaviour(country, 'tutorial_videos_link') && (
            <NavigationItem
              id="nav_user_manual"
              title={localized(locale, 'tutorial_videos')}
              picto={<TutorialVideosIcon />}
              onClick={(e) => {
                e.preventDefault();
                window.open(countryBehaviour(country, 'tutorial_videos_link'));
              }}
              active={!!countryBehaviour(country, 'tutorial_videos_link')}
            />
          )}
          <NavigationItem
            id="nav_service_desk"
            title={localized(locale, 'service_desk')}
            itemClass={location.pathname === '/service-desk' ? 'main-nav__link--active' : null}
            link="/service-desk"
            picto={<img src={ServiceDeskNG4} alt={localized(locale, 'service_desk')} />}
            active
          />
          <NavigationItem
            id="nav_faq"
            title={localized(locale, 'faq')}
            itemClass={location.pathname === '/faq' ? 'main-nav__link--active' : null}
            link="/faq"
            picto={<img src={FAQNG4} alt={localized(locale, 'faq')} />}
            active
          />
          {countryBehaviour(country, 'refer_a_farmer_functionality') && (
            <NavigationItem
              id="nav_refer_a_farmer"
              title={localized(locale, 'refer_a_farmer')}
              itemClass={location.pathname === '/refer-a-farmer' ? 'main-nav__link--active' : null}
              link="/refer-a-farmer"
              picto={<ReferFarmerIcon />}
              active={authenticated && role && !isOnboarding}
            />
          )}
        </div>
        {authenticated ? (
          <NavigationItem
            id="nav_logout"
            title={localized(locale, 'logout')}
            onClick={() => {
              history.replace('/welcome');
              logout();
            }}
            picto={<img src={Exit} alt={localized(locale, 'logout')} />}
            itemClass="main-nav__item--secondary"
            active
          />
        ) : (
          <NavigationItem
            id="nav_logout"
            title={localized(locale, 'homescreen')}
            onClick={() => {
              history.replace('/');
              resetUser();
              localStorage.clear();
            }}
            picto={<img src={Exit} alt={localized(locale, 'logout')} />}
            itemClass="main-nav__item--secondary"
            active
          />
        )}
      </nav>
    </Grid>
  );
};

Navigation.propTypes = {
  user: PropTypes.object,
  location: PropTypes.object,
  resetUser: PropTypes.func,
  toggleNavigation: PropTypes.func,
  history: PropTypes.object,
  myPlans: PropTypes.object,
  UI: PropTypes.object,
};

export default withRouter(
  connect(
    ({ user, myPlans, UI, toggleNavigation }) => ({
      user,
      myPlans,
      UI,
      toggleNavigation,
    }),
    {
      resetPlan,
      resetUser,
      toggleNavigation,
    },
  )(Navigation),
);
